// Lectura exclusivamente de fuentes anteriores. Nunca crea ni modifica el origen.
export const LEGACY_SOURCES = [
    ['coordinacion_visitas_offline', 'visita_offline_entries', 'ENTRIES'],
    ['coordinacion_visitas_offline', 'visita_offline_operations', 'OPERATIONS'],
    ['coordinacion_visitas_offline', 'visita_offline_media', 'MEDIA'],
    ['coordinacion_visitas_offline_operations', 'visita_offline_operations', 'OPERATIONS'],
    ['coordinacion_visitas_offline_media', 'visita_offline_media_entries', 'MEDIA'],
    ['coordinacion_visitas_offline', 'offline_file_entries', 'FILES'],
    ['coordinacion_visitas_offline', 'offline_gps_points', 'GPS'],
    ['coordinacion_visitas_offline', 'offline_work_shifts', 'SHIFTS'],
    ['coordinacion_visitas_offline', 'offline_rrhh_entries', 'RRHH'],
    ['coordinacion_visitas_offline', 'offline_rrhh_files', 'RRHH_FILES'],
];
export const LEGACY_LOCAL_QUEUES = ['react_visitas_offline_queue_v1', 'react_visitas_offline_queue_v2_fallback'];
// Un ACK acredita una subida, no la disponibilidad del archivo en la autoridad
// nueva. Solo un descarte explícito y sin estados contradictorios cierra esto.
export function legacyFileNeedsRecovery(raw) {
    const payload = typeof raw?.payload === 'string' ? JSON.parse(raw.payload) : raw?.payload;
    const states = [raw?.status, raw?.state, payload?.status, payload?.state].filter(value => value != null);
    return !states.length || !states.every(value => ['discarded', 'resolved_discarded'].includes(value));
}
export function legacyPositiveId(value) {
    return (typeof value === 'number' || typeof value === 'string' && /^[1-9]\d*$/.test(value))
        && Number.isSafeInteger(Number(value)) && Number(value) > 0 ? Number(value) : null;
}
export const legacyHash = async value => [...new Uint8Array(await crypto.subtle.digest('SHA-256',
    typeof value === 'string' ? new TextEncoder().encode(value) : value))].map(v => v.toString(16).padStart(2, '0')).join('');

export function unpackLegacyQueue(parsed) {
    const entries = parsed?.entries ?? parsed;
    if (!entries || typeof entries !== 'object') throw Error('Formato de respaldo desconocido.');
    const rows = Array.isArray(entries) ? entries : Object.values(entries);
    if (rows.some(row => !row || typeof row !== 'object' || Array.isArray(row))) throw Error('Formato de respaldo desconocido.');
    return rows;
}

async function canonical(value) {
    if (value instanceof Date) return {date: value.toISOString()};
    if (value instanceof Blob) return {binary: await legacyHash(await value.arrayBuffer()), size: value.size, type: value.type};
    if (value instanceof ArrayBuffer) return {binary: await legacyHash(value), size: value.byteLength};
    if (ArrayBuffer.isView(value)) return {binary: await legacyHash(new Uint8Array(value.buffer, value.byteOffset, value.byteLength)), size: value.byteLength};
    if (Array.isArray(value)) return Promise.all(value.map(canonical));
    if (value && typeof value === 'object') {
        if (![Object.prototype, null].includes(Object.getPrototypeOf(value))) throw Error('El origen contiene un formato que requiere recuperación específica.');
        const result = Object.create(null);
        for (const key of Object.keys(value).sort()) result[key] = await canonical(value[key]);
        return result;
    }
    return value === undefined ? {undefined: true} : value;
}
export async function legacySourceFingerprint(source, row) {
    return legacyHash(JSON.stringify([source, await canonical(row)]));
}

export async function readLegacyRecords(factory, name, store, timeoutMs = 10000) {
    if (!factory?.open) throw Error('No se pudo inventariar el almacenamiento anterior.');
    return new Promise((resolve, reject) => {
        let db, tx, absent = false, settled = false;
        const finish = (error, rows) => {if (settled) return; settled = true; clearTimeout(timer); db?.close(); error ? reject(error) : resolve(rows);};
        const timer = setTimeout(() => {try {tx?.abort();} catch {} finish(Error('Lectura anterior incompleta; no se supone vacía.'));}, timeoutMs);
        try {
            const request = factory.open(name);
            request.onupgradeneeded = () => {absent = true; request.transaction.abort();};
            request.onblocked = () => finish(Error('El origen está bloqueado.'));
            request.onerror = () => absent ? finish(null, []) : finish(request.error || Error('No se pudo leer el origen.'));
            request.onsuccess = () => {
                db = request.result; if (settled) {db.close(); return;}
                if (!db.objectStoreNames.contains(store)) {finish(null, []); return;}
                try {
                    tx = db.transaction(store, 'readonly'); let rows;
                    const read = tx.objectStore(store).getAll(); read.onsuccess = () => {rows = read.result;};
                    tx.oncomplete = () => Array.isArray(rows) ? finish(null, rows) : finish(Error('Inventario incompleto.'));
                    tx.onerror = tx.onabort = () => finish(tx.error || Error('Lectura interrumpida.'));
                } catch (error) {finish(error);}
            };
        } catch (error) {finish(error);}
    });
}

export async function collectLegacyRecoverySources({idb = globalThis.window?.indexedDB, local = globalThis.window?.localStorage} = {}) {
    const records = [], errors = [], contexts = [];
    for (const [name, store, type] of LEGACY_SOURCES) {
        const source = `${name}/${store}`;
        try {
            for (const raw of await readLegacyRecords(idb, name, store)) {
                if (/^context:\d+$/.test(String(raw?.key || ''))) {contexts.push({source, raw}); continue;}
                records.push({source, type, raw, fingerprint: await legacySourceFingerprint(source, raw)});
            }
        } catch (error) {errors.push({source, reason: error.message});}
    }
    for (const source of LEGACY_LOCAL_QUEUES) try {
        if (!local?.getItem) throw Error('No se pudo leer el respaldo local.');
        const text = local.getItem(source); if (text == null) continue;
        for (const raw of unpackLegacyQueue(JSON.parse(text))) records.push({source, type: 'ENTRIES', raw, fingerprint: await legacySourceFingerprint(source, raw)});
    } catch (error) {errors.push({source, reason: error.message});}
    try {
        if (!Number.isSafeInteger(local?.length) || typeof local.key !== 'function') throw Error('Inventario de contextos anteriores no disponible.');
        let count = 0;
        for (let index = 0; index < local.length; index++) {
            const key = local.key(index), match = key?.match(/^cached_context_(\d+)$/);
            if (!match) continue;
            if (++count > 200) throw Error('Hay más contextos anteriores de los que se pueden revisar en este lote.');
            const text = local.getItem(key); if (text == null) throw Error('Un contexto anterior cambió durante el inventario.');
            contexts.push({source: `localStorage/${key}`, raw: {key: `context:${match[1]}`, payload: JSON.parse(text)}});
        }
    } catch (error) {errors.push({source: 'legacy-contexts', reason: error.message});}
    return {records, contexts, errors};
}
